import { getPlatformLogo } from "../lib/platformLogos";

interface PlatformLogoProps {
  /** Connector ID or platform name — looked up in the logo registry. */
  platformId: string;
  /** Accessible label. Defaults to "{platformId} logo". */
  alt?: string;
  /** Tailwind size classes applied to both the image and the fallback icon. */
  className?: string;
  /** Extra classes applied only to the <img> element. */
  imgClassName?: string;
}

/**
 * Renders the registered logo for the given platform, or a generic
 * platform icon if no logo is found. Never throws; always renders.
 */
export function PlatformLogo({
  platformId,
  alt,
  className = "h-8 w-8",
  imgClassName,
}: Readonly<PlatformLogoProps>) {
  const logoUrl = getPlatformLogo(platformId);
  const label = alt ?? `${platformId} logo`;

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={label}
        className={[className, imgClassName, "object-contain"].filter(Boolean).join(" ")}
        style={{ mixBlendMode: "multiply" }}
        draggable={false}
      />
    );
  }

  return <GenericPlatformIcon className={className} aria-label={label} />;
}

function GenericPlatformIcon({
  className,
  "aria-label": ariaLabel,
}: Readonly<{
  className?: string;
  "aria-label"?: string;
}>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
    >
      <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 3v3M12 18v3M3 12h3M18 12h3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
